/**
 * Scene Engine
 *
 * Manages scene transitions, state, and rendering.
 * The heart of the Myst-like navigation experience.
 */

import type { Scene, Transition } from '@/types/scene';

interface SceneState {
  current: Scene | null;
  history: string[];
  discovered: Set<string>;
}

const state: SceneState = {
  current: null,
  history: [],
  discovered: new Set(),
};

const scenes = new Map<string, Scene>();

export function registerScene(scene: Scene): void {
  scenes.set(scene.id, scene);
}

export function getCurrentScene(): Scene | null {
  return state.current;
}

export async function navigateTo(sceneId: string, transition?: Partial<Transition>): Promise<void> {
  const scene = scenes.get(sceneId);
  if (!scene) {
    console.error(`Scene not found: ${sceneId}`);
    return;
  }

  const effect = transition?.effect ?? 'fade';
  const duration = transition?.duration ?? 800;

  // Execute transition
  await performTransition(effect, duration, () => {
    if (state.current) {
      state.history.push(state.current.id);
    }
    state.current = scene;
    renderScene(scene);
  });
}

export function goBack(): void {
  const previousId = state.history.pop();
  if (previousId) {
    void navigateTo(previousId, { effect: 'fade', duration: 400 });
  }
}

export function discoverSecret(secretId: string): boolean {
  if (state.discovered.has(secretId)) {
    return false; // Already discovered
  }
  state.discovered.add(secretId);
  saveProgress();
  return true;
}

export function hasDiscovered(secretId: string): boolean {
  return state.discovered.has(secretId);
}

async function performTransition(_effect: string, duration: number, callback: () => void): Promise<void> {
  const container = document.getElementById('scene-container');
  if (!container) return;

  const halfDuration = String(duration / 2);
  // Fade out
  container.style.transition = `opacity ${halfDuration}ms ease-in-out`;
  container.style.opacity = '0';

  await sleep(duration / 2);
  callback();

  // Fade in
  container.style.opacity = '1';
  await sleep(duration / 2);
}

function renderScene(scene: Scene): void {
  const container = document.getElementById('scene-container');
  if (!container) return;

  // Apply atmosphere
  const { atmosphere } = scene;
  container.style.background = atmosphere.background;

  if (atmosphere.backgroundImage) {
    container.style.backgroundImage = `url(${atmosphere.backgroundImage})`;
    container.style.backgroundSize = 'cover';
    container.style.backgroundPosition = 'center';
  } else {
    container.style.backgroundImage = 'none';
  }

  if (atmosphere.filter) {
    container.style.filter = atmosphere.filter;
  } else {
    container.style.filter = 'none';
  }

  // Noise backdrop — create layers if not present
  let noiseBackdrop = container.querySelector<HTMLDivElement>('.noise-backdrop');
  let noiseVignette = container.querySelector<HTMLDivElement>('.noise-vignette');
  if (!noiseBackdrop) {
    noiseBackdrop = document.createElement('div');
    noiseBackdrop.className = 'noise-backdrop';
    container.insertBefore(noiseBackdrop, container.firstChild);
  }
  if (!noiseVignette) {
    noiseVignette = document.createElement('div');
    noiseVignette.className = 'noise-vignette';
    noiseBackdrop.after(noiseVignette);
  }

  // Apply per-scene noise variations via CSS custom properties
  const filterName = atmosphere.noiseFilter ?? 'smooth';
  container.style.setProperty('--noise-filter', `url(#noise-${filterName})`);
  container.style.setProperty('--noise-hue', `${String(atmosphere.noiseHue ?? 0)}deg`);
  container.style.setProperty('--noise-brightness', String(atmosphere.noiseBrightness ?? 0.6));
  container.style.setProperty('--noise-opacity', String(atmosphere.noiseOpacity ?? 0.5));

  // Render scene content
  let content = container.querySelector<HTMLDivElement>('.scene-content');
  if (!content) {
    content = document.createElement('div');
    content.className = 'scene-content';
    container.appendChild(content);
  }

  content.innerHTML = scene.render ? scene.render() : '';

  // Persistent nav bar — inject once into scene-content, show/hide per scene
  let nav = content.querySelector<HTMLElement>('.site-nav');
  if (!nav) {
    nav = document.createElement('nav');
    nav.className = 'site-nav';
    nav.innerHTML = [
      '<span class="nav-link" data-interaction="nav-music">MUSIC</span>',
      '<span class="nav-link" data-interaction="nav-drip">DRIP</span>',
      '<span class="nav-link" data-interaction="nav-more">MORE</span>',
    ].join('');
  }
  content.prepend(nav);
  nav.hidden = scene.hideNav === true;

  // "Come Home" button — appears on hover below nav, hidden on home itself
  let homeBtn = content.querySelector<HTMLElement>('.come-home-btn');
  if (!homeBtn) {
    homeBtn = document.createElement('span');
    homeBtn.className = 'come-home-btn';
    homeBtn.dataset['interaction'] = 'back-home';
    homeBtn.textContent = 'COME HOME';
  }
  nav.after(homeBtn);
  homeBtn.hidden = scene.id === 'home' || scene.hideNav === true;

  // Mark the active nav link
  nav.querySelectorAll('.nav-link').forEach((link) => {
    const sceneId = (link as HTMLElement).dataset['interaction']?.replace('nav-', '');
    link.classList.toggle('active', sceneId === scene.id);
  });

  // Dispatch event for scene-specific setup
  globalThis.dispatchEvent(new CustomEvent('scene:loaded', { detail: scene }));
}

function saveProgress(): void {
  localStorage.setItem(
    'vampires_progress',
    JSON.stringify({
      currentScene: state.current?.id,
      history: state.history,
      discovered: Array.from(state.discovered),
    })
  );
}

export function loadProgress(): void {
  const saved = localStorage.getItem('vampires_progress');
  if (saved) {
    try {
      const data = JSON.parse(saved) as {
        currentScene?: string;
        history: string[];
        discovered: string[];
      };
      state.history = data.history;
      state.discovered = new Set(data.discovered);
      if (data.currentScene) {
        void navigateTo(data.currentScene);
      }
    } catch {
      // Corrupted localStorage — reset to defaults and clear
      console.warn('⚠️ Corrupted progress data — resetting to defaults');
      localStorage.removeItem('vampires_progress');
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
